import { Injectable } from '@angular/core';

import {
  Action,
  Selector,
  State,
  StateContext,
  StateToken,
  Store
} from '@ngxs/store';
import { EMPTY, tap } from 'rxjs';

import { MyWorldHttpService } from '@app/core/services/http/my-world-http.service';
import { CustomerState } from '@app/core/store/customer/customer.state';
import { ModalService } from '@app/core/ui/components/modal';
import { ToastService } from '@app/core/ui/components/toast';
import { LoadingStateEnum } from '@app/shared/enums/loading-state.enum';
import { catchRemoteError } from '@app/shared/rxjs/custom-operators';
import { RequestState } from '@app/shared/types/request-state';

import {
  DeleteMessageStickerAction,
  LoadMessageStickersAction,
  SaveNewMessageStickerAction,
  UpdateMessageStickerAction
} from './message-sticker.action';
import {
  MessageSticker,
  MessageStickerResponseModel,
  MessageStickerCreateModel,
  MessageStickerUpdateModel
} from './message-sticker.model';

export interface MessageStickerStateModel {
  messages: MessageSticker[];
  requestStatus: RequestState;
  newMessageStatus: RequestState;
}

const MESSAGES_STICKER_STATE_TOKEN = new StateToken<MessageStickerStateModel>(
  'message_stickers'
);

@State<MessageStickerStateModel>({
  name: MESSAGES_STICKER_STATE_TOKEN,
  defaults: {
    messages: [],
    requestStatus: {
      errors: [],
      status: LoadingStateEnum.idle
    },
    newMessageStatus: {
      errors: [],
      status: LoadingStateEnum.idle
    }
  }
})
@Injectable()
export class MessageStickerState {
  constructor(
    private http: MyWorldHttpService,
    private toast: ToastService,
    private store: Store,
    private modal: ModalService
  ) {}

  @Selector() static messages({ messages }: MessageStickerStateModel) {
    return messages ?? null;
  }

  @Selector() static requestStatus({
    requestStatus
  }: MessageStickerStateModel) {
    return requestStatus?.status;
  }

  @Selector() static newMessageStatus({
    newMessageStatus
  }: MessageStickerStateModel) {
    return newMessageStatus?.status;
  }

  @Action(LoadMessageStickersAction) loadMessageStickers({
    patchState
  }: StateContext<MessageStickerStateModel>) {
    patchState({
      requestStatus: {
        errors: [],
        status: LoadingStateEnum.loading
      }
    });

    return this.http.getMessageStickers().pipe(
      catchRemoteError(
        (remoteError) => {
          this.toast.danger(remoteError.message);
          patchState({
            messages: [],
            requestStatus: {
              errors: [],
              status: LoadingStateEnum.error
            }
          });
        },
        {
          defaultMessage:
            'Não foi possível encontrar as mensagens para este aluno. Por favor, tente novamente.'
        }
      ),
      tap((data) => {
        const messages = this.toMessageStickers(data);
        patchState({
          messages,
          requestStatus: {
            errors: [],
            status: LoadingStateEnum.loaded
          }
        });
      })
    );
  }

  // eslint-disable-next-line max-lines-per-function
  @Action(SaveNewMessageStickerAction) saveNewMessageSticker(
    { patchState }: StateContext<MessageStickerStateModel>,
    { payload }: SaveNewMessageStickerAction
  ) {
    patchState({
      newMessageStatus: {
        errors: [],
        status: LoadingStateEnum.loading
      }
    });

    const customerEmail = this.store.selectSnapshot(CustomerState.email);

    if (!customerEmail) {
      const errorMessage = 'Não foi possível encontrar o email do responsável';
      this.toast.danger(errorMessage);

      patchState({
        newMessageStatus: {
          errors: [
            {
              detail: errorMessage
            }
          ],
          status: LoadingStateEnum.error
        }
      });

      return EMPTY;
    }

    const payloadData: MessageStickerCreateModel = {
      ...payload,
      writerEmail: customerEmail
    };

    return this.http
      .saveMessageSticker(payloadData)
      .pipe(
        catchRemoteError(
          (remoteError) => {
            this.toast.danger(remoteError.message);
            patchState({
              newMessageStatus: {
                errors: [{ detail: remoteError.message }],
                status: LoadingStateEnum.error
              }
            });
          },
          {
            defaultMessage:
              'Não foi possível criar um novo recado. Tente novamente'
          }
        )
      )
      .subscribe(() => {
        patchState({
          newMessageStatus: {
            errors: [],
            status: LoadingStateEnum.loaded
          }
        });
        this.toast.success('A mensagem foi criada com sucesso');
        this.modal.close({ status: 'success' });
        this.store.dispatch(new LoadMessageStickersAction());
      });
  }

  // eslint-disable-next-line max-lines-per-function
  @Action(UpdateMessageStickerAction) updateMessageSticker(
    { patchState }: StateContext<MessageStickerStateModel>,
    { payload }: UpdateMessageStickerAction
  ) {
    patchState({
      newMessageStatus: {
        errors: [],
        status: LoadingStateEnum.loading
      }
    });

    const customerEmail = this.store.selectSnapshot(CustomerState.email);

    if (!customerEmail) {
      const errorMessage = 'Não foi possível encontrar o email do responsável';
      this.toast.danger(errorMessage);

      patchState({
        newMessageStatus: {
          errors: [
            {
              detail: errorMessage
            }
          ],
          status: LoadingStateEnum.error
        }
      });

      return EMPTY;
    }

    const payloadData: MessageStickerUpdateModel = {
      ...payload,
      writerEmail: customerEmail
    };

    return this.http
      .editMessageSticker(payloadData)
      .pipe(
        catchRemoteError(
          (remoteError) => {
            this.toast.danger(remoteError.message);
            patchState({
              newMessageStatus: {
                errors: [{ detail: remoteError.message }],
                status: LoadingStateEnum.error
              }
            });
          },
          {
            defaultMessage:
              'Não foi possível editar este recado. Tente novamente'
          }
        )
      )
      .subscribe(() => {
        patchState({
          newMessageStatus: {
            errors: [],
            status: LoadingStateEnum.loaded
          }
        });
        this.toast.success('O recado foi atualizado com sucesso');
        this.modal.close({ status: 'success' });
        this.store.dispatch(new LoadMessageStickersAction());
      });
  }

  // eslint-disable-next-line max-lines-per-function
  @Action(DeleteMessageStickerAction) deleteMessageSticker(
    { patchState }: StateContext<MessageStickerStateModel>,
    { payload }: DeleteMessageStickerAction
  ) {
    patchState({
      newMessageStatus: {
        errors: [],
        status: LoadingStateEnum.loading
      }
    });

    const customerEmail = this.store.selectSnapshot(CustomerState.email);

    if (!customerEmail) {
      const errorMessage = 'Não foi possível encontrar o email do responsável';
      this.toast.danger(errorMessage);

      patchState({
        newMessageStatus: {
          errors: [
            {
              detail: errorMessage
            }
          ],
          status: LoadingStateEnum.error
        }
      });

      return EMPTY;
    }

    return this.http.deleteMessageSticker(payload, customerEmail).pipe(
      catchRemoteError(
        (remoteError) => {
          this.toast.danger(remoteError.message);
          patchState({
            newMessageStatus: {
              errors: [{ detail: remoteError.message }],
              status: LoadingStateEnum.error
            }
          });
        },
        {
          defaultMessage:
            'Não foi possível deletar este recado. Tente novamente'
        }
      ),
      tap(() => {
        patchState({
          newMessageStatus: {
            errors: [],
            status: LoadingStateEnum.loaded
          }
        });
        this.toast.success('O recado foi excluído com sucesso');
      })
    );
  }

  private toMessageStickers(
    data: MessageStickerResponseModel[]
  ): MessageSticker[] {
    return data.map((message) => ({
      id: message?.id,
      writerEmail: message?.writerEmail,
      author: message?.exibitionName || '',
      message: message?.message || ''
    }));
  }
}
